import React from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import { ShipmentMapData } from "shared-ui/src/features/maps-feature/ShipmentMapProvider";

interface ShipmentMapProps {
  mapData?: ShipmentMapData | null;
  isLoading?: boolean;
  isError?: boolean;
  currentLocationLabel?: string | null;
  shipToAddress?: string[] | null;
}

const MAP_HEIGHT = 360;


export function ShipmentMap({
  mapData,
  isLoading = false,
  isError = false,
  currentLocationLabel,
  shipToAddress,
}: Readonly<ShipmentMapProps>) {

  if (isLoading) {
    return (
      <Box
        sx={{
          height: MAP_HEIGHT,
          bgcolor: "grey.100",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 1,
        }}
      >
        <Typography variant="body2" color="text.disabled">
          Loading map...
        </Typography>
      </Box>
    );
  }

  if (isError || !mapData?.mapSasUrl || mapData.width == null || mapData.height == null) {
    return (
      <Box
        sx={{
          height: MAP_HEIGHT,
          bgcolor: "grey.100",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 1,
        }}
        aria-label="Map unavailable"
      >
        <PlaceOutlinedIcon sx={{ fontSize: 40, color: "text.disabled" }} />
        <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
          Map unavailable
        </Typography>
      </Box>
    );
  }

  const currentTooltip = currentLocationLabel ? (
    <span>
      <strong>Current package location</strong>
      <br />
      {currentLocationLabel}
    </span>
  ) : (
    "Current package location"
  );

  const shipToTooltip = shipToAddress?.length ? (
    <span>
      <strong>Final destination</strong>
      <br />
      {shipToAddress.join("\n")}
    </span>
  ) : (
    "Final destination"
  );

  const currentPixelX = mapData.currentLocation?.pixelX ?? 0;
  const currentPixelY = mapData.currentLocation?.pixelY ?? 0;
  const destPixelX = mapData.destination?.pixelX ?? 0;
  const destPixelY = mapData.destination?.pixelY ?? 0;

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 800, // <- shrinks the whole thing; height follows
        mx: "auto",
        aspectRatio: `${mapData.width} / ${mapData.height}`,
        position: "relative",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <img
        src={mapData.mapSasUrl}
        alt="Shipment map"
        style={{
          width: "100%",
          height: "100%",
          display: "block",
          objectFit: "cover",
        }}
      />
      {/* Tooltip hotspots positioned at pixel coordinates returned from backend */}
      {mapData.currentLocation && (
        <Tooltip title={currentTooltip} placement="top" arrow>
          <Box
            sx={{
              position: "absolute",
              left: `${(currentPixelX / mapData?.width) * 100}%`,
              top: `${(currentPixelY / mapData?.height) * 100}%`,
              width: 30,
              height: 30,
              transform: "translate(-50%, -50%)",
              cursor: "pointer",
              pointerEvents: "auto",
            }}
            data-testid="current-location-hotspot"
          />
        </Tooltip>
      )}
      {mapData.destination && (
        <Tooltip title={shipToTooltip} placement="top" arrow>
          <Box
            sx={{
              position: "absolute",
              left: `${(destPixelX / mapData?.width) * 100}%`,
              top: `${(destPixelY / mapData?.height) * 100}%`,
              width: 30,
              height: 30,
              transform: "translate(-50%, -50%)",
              cursor: "pointer",
              pointerEvents: "auto",
            }}
            data-testid="destination-hotspot"
          />
        </Tooltip>
      )}
    </Box>
  );
}





















